import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@scio3/ui';
import { useSubscriptionModal } from '@/hooks/useSubscriptionModal';
import { useAuthStore } from '@/stores/auth-store';
import { analyticsUpgradeCta } from '@/lib/analytics-service';
import { toast } from 'sonner';

export interface PlanGateProps {
  /** Feature flag key passed to the subscription modal (e.g. 'samlSSO', 'apiKey'). */
  planFeature: string;
  /** Minimum plan required (e.g. 'proPlus', 'engage'). */
  minPlanRequired: string;
  /** Human-readable feature name shown in the message (e.g. 'SAML Single Sign On'). */
  featureLabel: string;
  /** Human-readable plan name shown in the message (e.g. 'Pro Plus Plan'). */
  planLabel: string;
  /** Optional "Learn more" link URL. Defaults to generic OptiSigns link. */
  learnMoreUrl?: string;
  /** Optional background image URL. When provided, shows a greyscale image behind the overlay. */
  backgroundImageUrl?: string;
}

/**
 * Reusable plan gate — shows an upgrade prompt when a feature is not available
 * on the user's current plan.
 *
 * Two modes:
 * - With `backgroundImageUrl`: full-area overlay with greyscale background image + semi-transparent
 *   black overlay + centered white text/button (matching scio1 plan gate design).
 * - Without: transparent overlay designed to sit on top of disabled page UI underneath.
 */
export function PlanGate({
  planFeature,
  minPlanRequired,
  featureLabel,
  planLabel,
  learnMoreUrl = 'https://links.optisigns.com/E2Ea',
  backgroundImageUrl,
}: PlanGateProps) {
  const { t } = useTranslation();
  const { openSubscriptionPlan } = useSubscriptionModal();
  const company = useAuthStore((s) => s.company);
  const isWhiteLabel = !!company?.brandingData?.isWhiteLabel;

  const handleUpgrade = useCallback(() => {
    const parentAccountId = (company as Record<string, unknown> | null)?.parentAccountId;
    if (parentAccountId) {
      toast.error(t('cannotUpgrade'));
      return;
    }
    analyticsUpgradeCta({
      ctaLocation: `plan_gate_${planFeature}`,
      currentPlan: company?.payment?.plan,
    });
    openSubscriptionPlan({
      title: t('Change Plan'),
      subtitle: t('Upgrade Required'),
      action: 'changePlanTrialToPay',
      sizeModal: 'xl',
      params: {
        planFeature,
        minPlanRequired,
        subtitle: t('Upgrade Required'),
        arrCssFile: true,
      },
    });
  }, [openSubscriptionPlan, company, t, planFeature, minPlanRequired]);

  const content = (
    <>
      <p className="text-center text-sm text-white">
        {t('{{feature}} is available for {{plan}}', {
          feature: t(featureLabel),
          plan: t(planLabel),
        })}
      </p>
      <Button size="lg" className="mt-3 px-7 text-sm" onClick={handleUpgrade}>
        {t('Upgrade Now')}
      </Button>
      {!isWhiteLabel && (
        <p className="mt-3 text-center text-sm text-white">
          <a
            href={learnMoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-white underline hover:text-white/80"
          >
            {t('Learn more')}
          </a>
          {' - '}
          {t('You can upgrade/downgrade anytime')}
        </p>
      )}
    </>
  );

  if (backgroundImageUrl) {
    return (
      <div className="relative h-full w-full overflow-hidden rounded-lg">
        <img
          src={backgroundImageUrl}
          alt=""
          className="absolute inset-0 h-full w-full object-cover object-top opacity-70 grayscale"
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-0 flex flex-col items-center justify-start pt-[20%]">
          {content}
        </div>
      </div>
    );
  }

  // Overlay mode — sits on top of disabled page UI
  return (
    <div className="absolute inset-0 z-50 flex flex-col items-center justify-start pt-[20%]">
      <div className="absolute inset-0 bg-black/70" />
      <div className="relative z-10 flex flex-col items-center">{content}</div>
    </div>
  );
}
